import { Box, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { MaskedCurrency } from '../MaskedValue';
import { resolveAmountColor } from '../AmountDisplay';
import { interpolate } from '../../utils/entryType';
import { shortMonthYear } from '../../utils/costOfLiving';
import { scrollToLedger } from '../../utils/dashboardScroll';

// The `/ledger-months` row each pick reads (#2208).
const FIELD = { spendingOutside: 'outside', transfers: 'transfers', outliers: 'outliers' };
// Ledger · line count | amount, as the board has it.
const COLUMNS = 'minmax(0, 1fr) 150px';

/**
 * Ledger breakdown (#2208, epic #2201): the picked Money in and out row
 * (Spending outside budgets, Transfers or Outliers) split by ledger for the
 * current month, in place of the Month to month budgets. One row per ledger
 * with a line of that kind, in name order, then a Total equal to the picked
 * row; the Total lights with that row (`highlight`, `onHighlight`).
 *
 * Amounts are neutral at any sign, zero dim; Spent reads positive (money out
 * is stored negative). A ledger with a row in Ledgers below is a link that
 * scrolls there and flashes the row; any other ledger (archived) is plain
 * text under the name its lines carry.
 *
 * @param {object} props
 * @param {('spendingOutside'|'transfers'|'outliers')} props.kind
 * @param {Array} props.ledgers         `/ledger-months` ledgers
 * @param {Array} props.pageLedgers     the active ledgers shown in Ledgers ({ ledgerId, type })
 * @param {string} props.yearMonth
 * @param {string|null} [props.highlight]
 * @param {(key: string|null) => void} [props.onHighlight]
 * @param {Object} props.strings        engine strings
 * @param {string} props.locale
 */
const LedgerBreakdown = ({ kind, ledgers, pageLedgers, yearMonth, highlight = null, onHighlight, strings, locale }) => {
  const theme = useTheme();
  const { palette, ds } = theme;
  const s = (key) => strings[`dashboardN.ledgers.${key}`];
  const title = interpolate(s(`title.${kind}`), { month: shortMonthYear(yearMonth, locale) });
  const field = FIELD[kind];
  const sign = kind === 'spendingOutside' ? -1 : 1;
  const rows = ledgers.filter((l) => l[field].lineCount > 0);
  const totalCents = rows.reduce((sum, l) => sum + l[field].cents, 0);
  const typeOf = new Map(pageLedgers.map((l) => [l.ledgerId, l.type]));

  const headerSx = { fontSize: '11px', letterSpacing: ds.font.letterUpper, textTransform: 'uppercase', color: palette.text.dim };
  const rowBorder = `1px solid ${palette.divider}`;
  const nameSx = { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
  const linkSx = {
    ...nameSx, border: 0, background: 'none', padding: 0, font: 'inherit', color: palette.interactive, cursor: 'pointer', textAlign: 'left',
    '&:hover': { textDecoration: 'underline' },
    '&:focus-visible': { outline: `1.5px solid ${palette.interactive}`, outlineOffset: '2px' },
  };
  const amount = (cents, total = false) => (
    <MaskedCurrency
      value={(sign * cents) / 100}
      style={{
        color: resolveAmountColor(palette, 'neutral', sign * cents < 0, cents === 0),
        fontVariantNumeric: 'tabular-nums',
        whiteSpace: 'nowrap',
        fontWeight: total ? ds.font.weightMedium : ds.font.weightRegular,
      }}
    />
  );
  const flashColor = alpha(palette.interactive, 0.16);

  const ledgerRow = (l) => {
    const onPage = typeOf.has(l.ledgerId);
    return (
      <Box
        key={l.ledgerId}
        data-testid={`ledger-row-${l.ledgerId}`}
        sx={{ display: 'grid', gridTemplateColumns: COLUMNS, columnGap: `${ds.space[6]}px`, alignItems: 'center', py: `${ds.space[3]}px`, borderBottom: rowBorder, fontSize: '13px' }}
      >
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: `${ds.space[2]}px`, minWidth: 0, pl: `${ds.space[5]}px`, color: palette.text.primary }}>
          {onPage
            ? (
              <Box
                component="button"
                type="button"
                data-testid="ledger-link"
                onClick={() => scrollToLedger(l.ledgerId, typeOf.get(l.ledgerId), flashColor, ds.motion.easeOut)}
                sx={linkSx}
              >
                {l.ledgerName}
              </Box>
            )
            : <Box component="span" data-testid="ledger-name" sx={nameSx}>{l.ledgerName}</Box>}
          <Box component="span" sx={{ fontSize: '13px', color: palette.text.dim, whiteSpace: 'nowrap' }}>{`· ${l[field].lineCount}`}</Box>
        </Box>
        <Box sx={{ textAlign: 'right' }}>{amount(l[field].cents)}</Box>
      </Box>
    );
  };

  const lit = highlight === kind;
  const pointer = onHighlight ? { onPointerEnter: () => onHighlight(kind), onPointerLeave: () => onHighlight(null) } : {};

  return (
    <Box component="section" aria-label={title} data-testid="ledger-breakdown" data-kind={kind}>
      <Typography
        component="h3"
        sx={{ m: 0, py: `${ds.space[3]}px`, fontSize: `${ds.font.meta}px`, fontWeight: ds.font.weightMedium, color: palette.text.secondary }}
      >
        {title}
      </Typography>
      <Box data-testid="ledger-header-row" sx={{ display: 'grid', gridTemplateColumns: COLUMNS, columnGap: `${ds.space[6]}px`, py: `${ds.space[3]}px`, borderBottom: rowBorder, ...headerSx }}>
        <Box sx={{ pl: `${ds.space[1]}px` }}>{s('colLedger')}</Box>
        <Box sx={{ textAlign: 'right' }}>{s(`col.${kind}`)}</Box>
      </Box>
      {rows.map(ledgerRow)}
      <Box
        data-testid="ledger-total-row"
        data-highlight={lit ? 'true' : undefined}
        {...pointer}
        sx={{
          display: 'grid', gridTemplateColumns: COLUMNS, columnGap: `${ds.space[6]}px`, alignItems: 'center', py: `${ds.space[3]}px`,
          fontSize: '13.5px', borderRadius: '4px',
          backgroundColor: lit ? alpha(palette.interactive, 0.10) : 'transparent',
          transition: `background-color ${ds.motion.fast} ${ds.motion.easeOut}`,
        }}
      >
        <Box sx={{ pl: `${ds.space[1]}px`, color: palette.text.primary, fontWeight: ds.font.weightMedium }}>{s('total')}</Box>
        <Box sx={{ textAlign: 'right' }}>{amount(totalCents, true)}</Box>
      </Box>
    </Box>
  );
};

export default LedgerBreakdown;
